import { useTranslation } from 'react-i18next';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { formatAmsterdamTijd } from '../amsterdamTijd';
import { isHandlingTimeGateClosed, type HandlingTimePerEtiketRij } from '../handlingTime.api';
import { useHandlingTimePerEtiket } from '../hooks/useHandlingTimePerEtiket';
import { naamVoorRegel } from '../naam';

/**
 * Vervolg behandeltijd-rapportage, CAP-4 — per persoon, per etiket.
 *
 * Eén rij per etiket, met drie dingen in drie kolommen die nooit opgeteld worden: het moment van
 * OPENEN, het moment van OPSLAAN en de bevestigde MINUTEN. Tijden zijn Amsterdamse tijd. Een
 * ontbrekend tijdstip staat als vertaalde tekst, nooit als een verzonnen klokuur.
 *
 * Dicht is het overzicht geen storing: de pagina toont er al één melding voor, en dit
 * onderdeel verdwijnt. Een lege periode is één regel, geen lege tabel.
 */

export interface PerEtiketOverzichtProps {
  from?: string;
  to?: string;
  /** Afwezig betekent alle GLN's. */
  gln?: string;
  /** Alleen zolang de periode en het GLN geldig zijn; anders wordt er niets opgehaald. */
  enabled: boolean;
}

/** Het GLN van een etiket: het eerste deel van `GLN-GTIN-doelmarkt`. */
function partijVan(tradeItemId: string): string {
  return tradeItemId.split('-')[0] ?? '';
}

/** De doelmarkt van een etiket: alles na het tweede streepje. */
function doelmarktVan(tradeItemId: string): string {
  return tradeItemId.split('-').slice(2).join('-');
}

/** Per persoon de etiketten, in de volgorde waarin de bron ze levert. */
function perPersoon(rijen: HandlingTimePerEtiketRij[]): HandlingTimePerEtiketRij[][] {
  const groepen = new Map<number, HandlingTimePerEtiketRij[]>();
  for (const rij of rijen) {
    const groep = groepen.get(rij.userId);
    if (groep) groep.push(rij);
    else groepen.set(rij.userId, [rij]);
  }
  return [...groepen.values()];
}

export function PerEtiketOverzicht({ from, to, gln, enabled }: PerEtiketOverzichtProps) {
  const { t, i18n } = useTranslation();
  const lezing = useHandlingTimePerEtiket({ from, to }, { enabled, gln });

  if (!enabled) return null;
  // Dicht is geen storing, en de pagina zegt dat al één keer. Hier dus niets.
  if (lezing.isError && isHandlingTimeGateClosed(lezing.error)) return null;

  const personen = perPersoon(lezing.data ?? []);

  return (
    <section className="space-y-4" data-testid="handling-time-per-etiket">
      <h2 className="text-base font-semibold tracking-tight text-[var(--foreground)]">
        {t('handlingTime.perEtiket.title')}
      </h2>

      {lezing.isLoading ? (
        <div className="skeleton h-8 rounded-lg" data-testid="handling-time-per-etiket-loading" />
      ) : lezing.isError ? (
        <ApiErrorMessage error={lezing.error} />
      ) : personen.length === 0 ? (
        <p className="text-sm text-[var(--foreground-muted)]" data-testid="handling-time-per-etiket-leeg">
          {t('handlingTime.perEtiket.leeg')}
        </p>
      ) : (
        personen.map((rijen) => (
          <div className="space-y-2" data-testid="handling-time-per-etiket-persoon" key={rijen[0]?.userId}>
            <h3 className="text-sm font-semibold text-[var(--foreground)]">{naamVoorRegel(rijen[0]!, t)}</h3>
            {/* Smal scherm: de tabel scrolt binnen zijn eigen kader, de pagina niet opzij. */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-[var(--foreground-muted)]">
                  <tr>
                    <th className="py-1 pr-3 font-medium">{t('handlingTime.perEtiket.kolom.etiket')}</th>
                    <th className="py-1 pr-3 font-medium">{t('handlingTime.perEtiket.kolom.geopend')}</th>
                    <th className="py-1 pr-3 font-medium">{t('handlingTime.perEtiket.kolom.opgeslagenOm')}</th>
                    <th className="py-1 pr-3 text-right font-medium">{t('handlingTime.perEtiket.kolom.minuten')}</th>
                    <th className="py-1 font-medium">{t('handlingTime.perEtiket.kolom.opgeslagen')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rijen.map((rij) => (
                    <tr className="border-t border-[var(--border)]" data-testid="handling-time-per-etiket-rij" key={rij.tradeItemId}>
                      <td className="py-1 pr-3 align-top">
                        <span className="block tabular-nums text-[var(--foreground)]">{rij.gtin}</span>
                        <span className="block text-xs text-[var(--foreground-muted)]">
                          {t('handlingTime.perEtiket.partijEnDoelmarkt', { gln: partijVan(rij.tradeItemId), doelmarkt: doelmarktVan(rij.tradeItemId) })}
                        </span>
                      </td>
                      <td className="py-1 pr-3 align-top tabular-nums">
                        {rij.geopendAt ? formatAmsterdamTijd(rij.geopendAt, i18n.language) : t('handlingTime.perEtiket.geenOpening')}
                      </td>
                      <td className="py-1 pr-3 align-top tabular-nums">
                        {rij.opgeslagenAt ? formatAmsterdamTijd(rij.opgeslagenAt, i18n.language) : t('handlingTime.perEtiket.geenOpslag')}
                      </td>
                      <td className="py-1 pr-3 text-right align-top tabular-nums">{rij.minuten}</td>
                      <td className="py-1 align-top">
                        {rij.opgeslagen ? t('handlingTime.perEtiket.ja') : t('handlingTime.perEtiket.nee')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </section>
  );
}
